import { BaseComponent } from './base.component';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { FormFields } from '../../../types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Directive, EventEmitter, inject, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { SelectComponent } from '../../../../shared/components/select/select.component';
import { FormControlComponent } from '../../../../shared/components/form-control/form-control.component';
import { DOCUMENT } from '@angular/common';

@Directive()
export abstract class BaseFormComponent<T = null> extends BaseComponent {

  @ViewChildren(FormControlComponent) formControlComponents: QueryList<FormControlComponent>;
  @ViewChildren(SelectComponent) selectComponents: QueryList<SelectComponent>;

  @Input() data: T;

  @Output() submitting = new EventEmitter<boolean>(false);
  @Output() formFinished = new EventEmitter<any>();
  @Output() formErrorOccurred = new EventEmitter<void>();

  public isError = false;
  public form!: FormGroup;
  public shouldPatchDataOnBuild = true;

  protected readonly updateOn: 'blur' | 'change' | 'submit' = 'blur';
  protected readonly abstract formFields: FormFields;
  protected readonly formBuilder = inject(FormBuilder);

  private _isSubmitting = false;
  private readonly _document = inject(DOCUMENT);

  public ngOnInit(): void {
    this.buildForm();
    this.formFinished.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.form.markAsPristine());
  }

  public get isRequiredFormFieldsErrorAlertVisible(): boolean {
    let hasError = false;

    this.formControlsActions((control: FormControl) => {
      if (!hasError && control.errors && control.errors['required'] && control.dirty) {
        hasError = true;
      }
    });

    return hasError;
  }

  public get isSubmitting(): boolean {
    return this._isSubmitting;
  }

  public set isSubmitting(value: boolean) {
    this._isSubmitting = value;
    this.cdr.detectChanges();
  }

  public addControlToFormArray(name: string, val: FormGroup): void {
    this.getArrayControl(name).push(val);
  }

  public removeControlFromFormArray(name: string, i: number): void {
    this.getArrayControl(name).removeAt(i);
    this.cdr.detectChanges();
  }

  public getArrayControl(name: string): FormArray {
    return this.form.get(name) as FormArray;
  }

  public getControl(name: string): AbstractControl {
    return this.fc[name];
  }

  public getControlErrorState(controlName: string, errorKey: string): boolean {
    if (this.getControl(controlName)) {
      const { errors } = this.getControl(controlName);

      return errors ? errors[errorKey] : false;
    }

    return false;
  }

  public getArrayControlErrorState(arrayName: string, index: number, name: string, errorKey: string): boolean {
    const { errors } = this.getArrayControl(arrayName).at(index).get(name);

    return errors ? errors[errorKey] : false;
  }

  public get fc(): Record<string, AbstractControl> {
    return this.form.controls;
  }

  public onSubmit(): void {
    if (this._document.activeElement) {
      (this._document.activeElement as HTMLElement).blur();
    }

    if (this.form.valid) {
      this.lockForm();
      this.formValidActions();
    } else {
      this.formErrorOccurred.emit();
      this.formControlsActions(control => {
        control.markAsDirty();
        control.updateValueAndValidity();
      });
      this.formErrorActions();
    }

    this.cdr.markForCheck();
  }

  public lockForm(): void {
    this.isSubmitting = true;
    this.submitting.emit(this.isSubmitting);
    this.form.disable();
    this.cdr.detectChanges();
  }

  public unlockForm(): void {
    this.isSubmitting = false;
    this.submitting.emit(this.isSubmitting);
    this.form.enable();
    this.cdr.detectChanges();
  }

  public resetForm(): void {
    this.selectComponents.forEach(x => x.reset());
    this.form.enable();
    this.form.reset();
    this.buildForm();
    this.isSubmitting = false;
    this.submitting.emit(this.isSubmitting);

    this.formControlsActions(control => {
      control.markAsPristine();
      control.updateValueAndValidity();
    });

    this.cdr.detectChanges();
  }

  public clearFormArray(name: string): void {
    const fFormArray = this.getArrayControl(name);

    while (fFormArray.length !== 0) {
      fFormArray.removeAt(0);
    }
  }

  public hasControlErrors(name: string): boolean {
    const control = this.getControl(name);

    return control.invalid && control.dirty;
  }


  public hasArrayControlErrors(arrayName: string, index: number, name: string): boolean {
    const control = this.getArrayControl(arrayName).at(index).get(name);

    return control.invalid && control.dirty;
  }

  protected formErrorActions(): void {}

  protected abstract formValidActions(): void;

  protected buildForm(): void {
    this.form = this.formBuilder.group({}, { updateOn: this.updateOn });

    for (const key in this.formFields) {
      if (this.formFields.hasOwnProperty(key)) {
        this.form.addControl(key, this.formFields[key]);
      }
    }

    if (this.data && this.shouldPatchDataOnBuild) {
      this.form.patchValue(this.data);
    }
  }

  private formControlsActions(
    cb: (control: FormControl) => void,
    controls: FormFields = this.form.controls as FormFields
  ): void {
    const abstractControls = Object.values(controls);

    abstractControls.forEach(abstractControl => {
      if (abstractControl instanceof FormControl) {
        cb(abstractControl as FormControl);
      } else if (abstractControl instanceof FormGroup) {
        this.formControlsActions(cb, (abstractControl as FormGroup).controls as FormFields);
      } else if (abstractControl instanceof FormArray) {
        this.formControlsActions(cb, (abstractControl as FormArray).controls as unknown as FormFields);
      }
    });
  }
}
